<template>

    <kt-modal 
        :visible="recordVisible" 
        :title="recordTitle"
        :size="'modal-xl'" 
        @dismiss="onDismiss()"
    >
        <template #body>
            <pm-form
                v-if="isPM"
                v-model="pmRecord"
            ></pm-form>
            <job-form
                v-else 
                v-model="record"
            ></job-form>
        </template>

        <template #footer>
            <div class="row w-100">
                <div :class="{ 'col-4': (record.Id || pmRecord.Id), 'col-6': !(record.Id || pmRecord.Id) }">
                    <button @click="onDismiss()" class="btn btn-sm btn-outline-secondary form-control">Close</button>
                </div>
                <div class="col-4" v-if="record.Id || pmRecord.Id">
                    <button @click="deleteRecord()" class="btn btn-sm btn-outline-danger form-control">Delete</button>
                </div>
                <div :class="{ 'col-4': (record.Id || pmRecord.Id), 'col-6': !(record.Id || pmRecord.Id) }">
                    <button @click="saveRecord()" class="btn btn-sm btn-outline-primary form-control">Save</button>
                </div>
            </div>
        </template>
    </kt-modal>

</template>

<script lang="ts" setup>

import { ref, watch, onUnmounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import Api from "@/services/Api";

import Emitter from "@/services/Emitter";
import ApiService from "@/services/ApiService";
import { JobModel } from "@/models/RequestModel";
import { PMModel } from "@/models/PMModel";

import Alert from "@/services/Alert";
import Helper from "@/services/Helper";
import JobForm from "@/components/Jobs/JobForm.vue";
import PmForm from "@/components/PMs/PMForm.vue";
import KtModal from "@/components/UI/KtModal.vue";

const route = useRoute();
const router = useRouter();
const recordTitle = ref("Job Entry");
const recordVisible = ref(true);
const isPM = ref(false);
const pmRecord = ref<PMModel>(new PMModel());
const unwatch:Array<Function> = [];

const emit = defineEmits(['close']);

const newRecord = ():JobModel => 
{
    return new JobModel();

};

const record = ref<JobModel>(newRecord());

const saveRecord = async () => {
    let data;
    
    if (isPM.value) {
        // Save as PM
        data = await ApiService.savePM(pmRecord.value);
    } else {
        // Save as regular request
        if (!record.value.Technician)
        {
            Alert.notify({
                text: "Please select a technician."
            });
            return;
        }

        console.debug("save", record.value);
        
        data = await ApiService.saveRequest(record.value);
    }
    
    // Check if we're in a route-based context or global modal
    if (route.name && route.name.toString().includes('Job')) {
        if (isPM.value) {
            Emitter.emit("redirect", "/pm/edit/" + data.Id);
        } else {
            Emitter.emit("redirect", "/job/edit/" + data.Id);
        }
    } else {
        // Global modal - close after save
        Alert.notify({
            text: isPM.value ? "PM saved successfully!" : "Request saved successfully!",
            type: "success"
        });
        emit('close');
    }
};

const deleteRecord = async () => {
    Alert.notify({
        text: "Deletion is currently disabled"
    });
};


const onDismiss = () => {
    // Check if we're in a route-based context (has ID in route) or global modal
    if (route.params.id && route.name && route.name.toString().includes('Job')) {
        // Use router.back() to preserve filters/query params from previous page
        router.back();
    } else {
        // Global modal - emit close event
        emit('close');
    }
};

const loadRecords = async (val:string) =>
{
    switch (val) {
        case "new":
            record.value = newRecord();
            recordTitle.value = "Job Entry";
            isPM.value = false;
            break;

        default:
            let rec = await ApiService.loadRequest(val + "");
            console.debug("Loaded job:", rec);
            
            // Check if this Request is linked to a PM - if so, show PM form instead
            if (rec.Id) {
                try {
                    const linkedPM = await ApiService.loadPMByRequest(rec.Id);
                    if (linkedPM && linkedPM.Id) {
                        console.log("Request is linked to PM, showing PM form:", linkedPM.Id);
                        // Show PM form instead of job form
                        isPM.value = true;
                        pmRecord.value = linkedPM;
                        recordTitle.value = "PM Entry";
                        return;
                    }
                } catch (error) {
                    console.debug("No PM linked to this request, proceeding normally");
                }
            }
            
            // Regular request
            isPM.value = false;
            record.value = rec;

            // Only show ID info if we have an actual record
            if (rec && rec.Id) {
                recordTitle.value = "ID" + rec.Id + " " + (rec.Contact?.Code || "") + " - " + (rec.RequestDetailedDescription || "");
            } else {
                recordTitle.value = "Job Entry";
            }



            break;
    }
};


unwatch.push(watch(() => route.params.id, (nv, ov) => {
    loadRecords(nv + "");
}, { immediate: true }));

onUnmounted(() =>
{
    unwatch.forEach((rw) => {
        rw();
    });
})

</script>